import React, { useEffect, useRef, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonFooter, IonIcon, IonInput, IonText, IonSpinner, IonLabel, IonAlert,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { apiService } from '@/services/ApiService';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';

interface AddModuleModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    onSaved: () => void;
}

interface FormData {
    name: string;
    col1: string;
    col2: string;
    col3: string;
    col4: string;
}

const INITIAL: FormData = {
    name: '',
    col1: 'Major Requirement',
    col2: 'Related Information',
    col3: 'Estimated Quantity',
    col4: 'Performance Standard',
};

const AddModuleModal: React.FC<AddModuleModalProps> = ({ isOpen, onDidDismiss, onSaved }) => {
    const [formData, setFormData] = useState<FormData>(INITIAL);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; variant: ToasterVariant } | null>(null);
    const forceClosingRef = useRef(false);

    useEffect(() => {
        if (isOpen) {
            forceClosingRef.current = false;
            setFormData({ ...INITIAL });
            setErrors({});
        }
    }, [isOpen]);

    const update = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validate = (): boolean => {
        const e: Record<string, string> = {};
        if (!formData.name.trim()) e.name = 'Module Name is required.';
        if (!formData.col1.trim()) e.col1 = 'Column Title 1 is required.';
        if (!formData.col2.trim()) e.col2 = 'Column Title 2 is required.';
        if (!formData.col3.trim()) e.col3 = 'Column Title 3 is required.';
        if (!formData.col4.trim()) e.col4 = 'Column Title 4 is required.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await apiService.addModule({
                Name: formData.name,
                LineItemColumnTitle1: formData.col1,
                LineItemColumnTitle2: formData.col2,
                LineItemColumnTitle3: formData.col3,
                LineItemColumnTitle4: formData.col4,
            });
            setToast({ message: 'Module created successfully.', variant: 'success' });
            onSaved();
            forceClosingRef.current = true;
            onDidDismiss();
        } catch {
            setToast({ message: 'Failed to create module.', variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const [showCancelAlert, setShowCancelAlert] = useState(false);
    const hasChanges = () => JSON.stringify(formData) !== JSON.stringify(INITIAL);

    const handleCancel = () => {
        if (hasChanges()) setShowCancelAlert(true);
        else onDidDismiss();
    };

    const label = (text: string) => (
        <IonLabel style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)', display: 'block', marginBottom: '0.2rem' }}>{text}</IonLabel>
    );

    const field = (key: keyof FormData, lbl: string) => (
        <div style={{ marginBottom: '0.65rem' }}>
            {label(lbl)}
            <IonInput value={formData[key]} onIonInput={e => update(key, e.detail.value ?? '')}
                style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} />
            {errors[key] && <IonText color="danger" style={{ fontSize: '0.75rem' }}>{errors[key]}</IonText>}
        </div>
    );

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={() => { if (!forceClosingRef.current) onDidDismiss(); }}
                style={{ '--width': '480px', '--height': '460px', '--border-radius': '8px' }}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ fontSize: '1rem' }}>Add Module</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={handleCancel}><IonIcon icon={closeOutline} /></IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent style={{ '--padding-start': '1.25rem', '--padding-end': '1.25rem', '--padding-top': '1rem' }}>
                    {isSaving ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><IonSpinner /></div>
                    ) : (
                        <div>
                            {field('name', 'Module Name *')}
                            {field('col1', 'Line Item Column Title 1 *')}
                            {field('col2', 'Line Item Column Title 2 *')}
                            {field('col3', 'Line Item Column Title 3 *')}
                            {field('col4', 'Line Item Column Title 4 *')}
                        </div>
                    )}
                </IonContent>

                {!isSaving && (
                    <IonFooter>
                        <IonToolbar style={{ '--padding-start': '1rem', '--padding-end': '1rem' }}>
                            <IonButtons slot="start">
                                <IonButton color="medium" onClick={handleCancel}>Cancel</IonButton>
                            </IonButtons>
                            <IonButtons slot="end">
                                <IonButton fill="solid" color="primary" onClick={handleSave}>Create Module</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonFooter>
                )}
            </IonModal>

            <IonAlert isOpen={showCancelAlert} header="Discard Changes" message="Discard your unsaved changes?"
                buttons={[
                    { text: 'Keep Editing', role: 'cancel', handler: () => setShowCancelAlert(false) },
                    { text: 'Discard', role: 'destructive', handler: () => { setShowCancelAlert(false); onDidDismiss(); } },
                ]}
                onDidDismiss={() => setShowCancelAlert(false)}
            />
            <Toaster isOpen={!!toast} message={toast?.message} variant={toast?.variant} onDidDismiss={() => setToast(null)} />
        </>
    );
};

export default AddModuleModal;
